import React from 'react';

/**
 * Higher-Order Component for navigation blocking in class components
 * @param {React.Component} WrappedComponent - The component to add navigation blocking to
 * @returns {React.Component} - Enhanced component with navigation blocking
 */
export const withNavigationBlocking = (WrappedComponent) => {
  return class NavigationBlockingWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        showNavigationConfirm: false,
        pendingNavigation: null,
      };
      this.isBlockingNavigation = false;
      this.shouldBlockNavigation = true;
      this._hasSetupHistory = false;
    }

    componentDidMount() {
      this._setupNavigationBlocking();
    }

    componentWillUnmount() {
      this._cleanupNavigationBlocking();
    }

    _setupNavigationBlocking = () => {
      window.addEventListener('beforeunload', this._blockUrlNavigation);
      window.addEventListener('popstate', this._handlePopState);

      // Setup initial history state
      if (!this._hasSetupHistory) {
        window.history.pushState(null, '', window.location.pathname);
        this._hasSetupHistory = true;
      }
    };

    _cleanupNavigationBlocking = () => {
      window.removeEventListener('beforeunload', this._blockUrlNavigation);
      window.removeEventListener('popstate', this._handlePopState);
    };

    _blockUrlNavigation = (e) => {
      if (this.shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    _handlePopState = (event) => {
      if (this.isBlockingNavigation || !this.shouldBlockNavigation) {
        return;
      }

      this.isBlockingNavigation = true;

      setTimeout(() => {
        window.history.pushState(null, '', window.location.pathname);
        this.setState({
          showNavigationConfirm: true,
          pendingNavigation: 'back',
        });

        setTimeout(() => {
          this.isBlockingNavigation = false;
        }, 50);
      }, 0);
    };

    // Methods to control navigation blocking
    enableNavigationBlocking = () => {
      this.shouldBlockNavigation = true;
    };

    disableNavigationBlocking = () => {
      this.shouldBlockNavigation = false;
    };

    triggerNavigationConfirm = (destination) => {
      this.setState({
        showNavigationConfirm: true,
        pendingNavigation: destination,
      });
    };

    confirmNavigation = () => {
      const { pendingNavigation } = this.state;
      this.setState({
        showNavigationConfirm: false,
        pendingNavigation: null,
      });
      return pendingNavigation;
    };

    cancelNavigation = () => {
      this.setState({
        showNavigationConfirm: false,
        pendingNavigation: null,
      });
    };

    render() {
      const navigationBlockingProps = {
        // Navigation blocking state
        showNavigationConfirm: this.state.showNavigationConfirm,
        pendingNavigation: this.state.pendingNavigation,

        // Navigation blocking methods
        enableNavigationBlocking: this.enableNavigationBlocking,
        disableNavigationBlocking: this.disableNavigationBlocking,
        triggerNavigationConfirm: this.triggerNavigationConfirm,
        confirmNavigation: this.confirmNavigation,
        cancelNavigation: this.cancelNavigation,
      };

      return <WrappedComponent {...this.props} {...navigationBlockingProps} />;
    }
  };
};

export default withNavigationBlocking;
